import { FeatureFlag, FeatureFlagGroup } from '../../../../../Models/FeatureFlags';
import { TranslationService } from '../../Services/translation.service';
import { fuzzyMatch, FuzzyMatchOptions } from '../../../../../Common/search';

const DISPLAY_NAME_FUZZY_OPTIONS: FuzzyMatchOptions = {
    fuzzyFallbackMaxLength: 20,
};

export function filterFlagsBySearch(
    flags: FeatureFlag[],
    query: string,
    displayNames: Map<string, string>
): FeatureFlag[] {
    if (!query || query.trim() === '') {
        return flags;
    }
    return flags.filter(flag =>
        fuzzyMatch(query, flag.Name) ||
        fuzzyMatch(query, displayNames.get(flag.LabelAlias) ?? '', DISPLAY_NAME_FUZZY_OPTIONS)
    );
}

export async function resolveDisplayNames(
    featureFlags: ReadonlyArray<Pick<FeatureFlag, 'LabelAlias'>>,
    translationService: TranslationService
): Promise<Map<string, string>> {
    const settled = await Promise.allSettled(
        featureFlags.map(async flag => ({
            labelAlias: flag.LabelAlias,
            displayName: await translationService.getTranslation(flag.LabelAlias, 'Text').toPromise(),
        }))
    );

    const displayNames = new Map<string, string>();
    for (const result of settled) {
        if (result.status === 'fulfilled' && result.value.displayName !== undefined) {
            displayNames.set(result.value.labelAlias, result.value.displayName);
        }
    }
    return displayNames;
}

// Sorts groups alphabetically by the label shown in the dropdown, falling back to the
// group name, so a group without a translation or a name still sorts instead of throwing.
export function sortGroupsByDisplayName(
    groups: FeatureFlagGroup[],
    displayNames: Map<string, string>
): FeatureFlagGroup[] {
    const label = (group: FeatureFlagGroup) => displayNames.get(group.LabelAlias) || group.Name || '';
    return [...groups].sort((a, b) => label(a).localeCompare(label(b), undefined, { sensitivity: 'base' }));
}
